import React, { useCallback, useEffect, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import mesApi, { FlourLotAnalysis } from "../../../Api/mes.api";
import FlourQualityShell from "./components/FlourQualityShell";
import DeviceMeasurementsPanel from "./components/DeviceMeasurementsPanel";
import {
  deviceSummaryBadge,
  formatHrDate,
  formatHrDateTime,
  formatNum,
  lotStatusLabel,
} from "./flourQualityUtils";
import { t } from "../../../i18n/locale";

type AnalysisTab = "pregled" | "blend" | "farinograph" | "extensograph" | "amylograph" | "svi-testovi" | "qc";

const TABS: { id: AnalysisTab; label: string }[] = [
  { id: "pregled", label: "Pregled" },
  { id: "blend", label: "Blend" },
  { id: "farinograph", label: "Farinograf" },
  { id: "extensograph", label: "Extensograf" },
  { id: "amylograph", label: "Amylograf" },
  { id: "svi-testovi", label: "Svi testovi" },
  { id: "qc", label: "QC odluke" },
];

const FlourQualityLotPage = () => {
  const { lotId: lotIdParam } = useParams<{ lotId: string }>();
  const lotId = Number(lotIdParam);
  const history = useHistory();

  const [analysis, setAnalysis] = useState<FlourLotAnalysis | null>(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState<AnalysisTab>("pregled");
  const [requireValid, setRequireValid] = useState(false);
  const [qcNotes, setQcNotes] = useState("");

  const load = useCallback(async () => {
    if (!lotId || Number.isNaN(lotId)) return;
    setLoading(true);
    try {
      const data = await mesApi.getFlourLotAnalysis(lotId);
      setAnalysis(data);
    } catch (err: any) {
      toast.error(err.message || t("Greška pri učitavanju analize"));
      setAnalysis(null);
    } finally {
      setLoading(false);
    }
  }, [lotId]);

  useEffect(() => {
    load();
  }, [load]);

  const handleRelease = async () => {
    if (!window.confirm(t("Puštate LOT u zalihu (released)?"))) return;
    try {
      await mesApi.releaseFlourQualityLot(lotId, "operator", qcNotes || undefined, requireValid);
      toast.success(t("LOT pušten"));
      history.push("/mes/flour-analyses/history");
    } catch (err: any) {
      toast.error(err.message);
    }
  };

  const handleReject = async () => {
    if (!window.confirm(t("Odbijate LOT?"))) return;
    try {
      await mesApi.rejectFlourQualityLot(lotId, "operator", qcNotes || undefined);
      toast.success(t("LOT odbijen"));
      history.push("/mes/flour-analyses/history");
    } catch (err: any) {
      toast.error(err.message);
    }
  };

  const lot = analysis?.lot;
  const lotLabel =
    lot?.internal_lot_code || lot?.lot_number_supplier || (lotId ? `LOT #${lotId}` : "");
  const st = lot ? lotStatusLabel(lot.status) : null;
  const isQuarantine = lot?.status === "quarantine";

  return (
    <FlourQualityShell analysisMode lotLabel={lotLabel}>
      <ToastContainer position="top-center" autoClose={2500} />

      {loading && <p className="mes-hint">{t("Učitavanje analize…")}</p>}
      {!loading && !analysis && (
        <p className="mes-hint">{t("Analiza nije dostupna.")}</p>
      )}

      {analysis && lot && (
        <>
          <header className="fq-lot-header mes-section">
            <div className="fq-lot-header-main">
              <h2 className="fq-lot-title">{lotLabel}</h2>
              {st && <span className={st.className}>{st.label}</span>}
            </div>
            <dl className="fq-lot-facts">
              <div>
                <dt>{t("Sirovina")}</dt>
                <dd>{lot.zboogie_ingredients?.ingredient_name || "—"}</dd>
              </div>
              <div>
                <dt>{t("Dobavljač")}</dt>
                <dd>{lot.zboogie_suppliers?.name || "—"}</dd>
              </div>
              <div>
                <dt>{t("Primka")}</dt>
                <dd>
                  {analysis.delivery_receipts?.[0]?.delivery_note_number || "—"}
                  {analysis.delivery_receipts?.[0]?.received_date && (
                    <span className="fq-muted"> ({formatHrDate(analysis.delivery_receipts[0].received_date)})</span>
                  )}
                </dd>
              </div>
              <div>
                <dt>{t("Količina")}</dt>
                <dd className="fq-num">{formatNum(lot.quantity_received_kg)} kg</dd>
              </div>
              <div>
                <dt>{t("Blend")}</dt>
                <dd>
                  <code>#{analysis.blend.id}</code>
                  <span className="fq-muted"> · {analysis.blend.blend_hash}</span>
                </dd>
              </div>
            </dl>
          </header>

          {isQuarantine && (
            <div className="fq-qc-bar mes-section">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={requireValid}
                  onChange={(e) => setRequireValid(e.target.checked)}
                />
                {t("Zahtijevaj valjano mjerenje pri puštanju")}
              </label>
              <input
                type="text"
                className="filter-input fq-qc-notes"
                placeholder={t("Napomena QC (opcionalno)")}
                value={qcNotes}
                onChange={(e) => setQcNotes(e.target.value)}
              />
              <button type="button" className="btn-primary btn-sm" onClick={handleRelease}>
                {t("Odobri")}
              </button>
              <button type="button" className="btn-secondary btn-sm" onClick={handleReject}>
                {t("Odbij")}
              </button>
            </div>
          )}

          <div className="mes-section">
            <div className="kpi-tabs fq-analysis-tabs">
              {TABS.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  className={`kpi-tab ${tab === item.id ? "active" : ""}`}
                  onClick={() => setTab(item.id)}
                >
                  {t(item.label)}
                </button>
              ))}
            </div>
          </div>

          {tab === "pregled" && (
            <div className="fq-tab-panel">
              <div className="fq-device-grid mes-section">
                {(["farinograph", "extensograph", "amylograph"] as const).map((d) => {
                  const sum = analysis.measurements[d];
                  const badge = deviceSummaryBadge(sum);
                  return (
                    <button
                      key={d}
                      type="button"
                      className="fq-overview-card"
                      onClick={() => setTab(d)}
                    >
                      <span className="fq-overview-card-title">
                        {d === "farinograph" ? t("Farinograf") : d === "extensograph" ? t("Extensograf") : t("Amylograf")}
                      </span>
                      <span className={badge.className}>{badge.label}</span>
                      <span className="fq-overview-stat">{t("{count} mjerenja", { count: sum.count })}</span>
                      {sum.latest_measured_at && (
                        <span className="fq-muted">
                          {t("Zadnje: {when}", { when: formatHrDateTime(sum.latest_measured_at) })}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
              <div className="mes-section">
                <h3>{t("Ukratko")}</h3>
                <p className="mes-hint">
                  {t("Ukupno testova na blendu:")} <strong>{analysis.all_tests.length}</strong>.{" "}
                  {t("Pregledajte pojedinačne uređaje ili karticu „Svi testovi” za kronološki popis.")}
                </p>
              </div>
            </div>
          )}

          {tab === "blend" && (
            <div className="fq-tab-panel mes-section">
              <h3>{t("Sastav blend-a")}</h3>
              <div className="mes-table-wrap">
                <table className="mes-table">
                <thead>
                  <tr>
                    <th>{t("LOT")}</th>
                    <th>{t("Sirovina")}</th>
                    <th>{t("Dobavljač")}</th>
                    <th>{t("Udio %")}</th>
                    <th>{t("kg primljeno")}</th>
                  </tr>
                </thead>
                <tbody>
                  {analysis.blend.zboogie_blend_components?.map((c) => (
                    <tr key={c.id}>
                      <td>
                        <strong>
                          {c.zboogie_material_lots?.internal_lot_code ||
                            c.zboogie_material_lots?.lot_number_supplier ||
                            c.lot_id}
                        </strong>
                      </td>
                      <td>{c.zboogie_material_lots?.zboogie_ingredients?.ingredient_name || "—"}</td>
                      <td>{c.zboogie_material_lots?.zboogie_suppliers?.name || "—"}</td>
                      <td className="fq-num">{formatNum(c.proportion_pct, 1)}</td>
                      <td className="fq-num">
                        {formatNum(c.zboogie_material_lots?.quantity_received_kg)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
            </div>
          )}

          {tab === "farinograph" && (
            <DeviceMeasurementsPanel
              device="farinograph"
              label="Farinograf"
              measurements={analysis.devices.farinograph}
            />
          )}
          {tab === "extensograph" && (
            <DeviceMeasurementsPanel
              device="extensograph"
              label="Extensograf"
              measurements={analysis.devices.extensograph}
            />
          )}
          {tab === "amylograph" && (
            <DeviceMeasurementsPanel
              device="amylograph"
              label="Amylograf"
              measurements={analysis.devices.amylograph}
            />
          )}

          {tab === "svi-testovi" && (
            <div className="fq-tab-panel mes-section">
              <h3>{t("Svi testovi na ovom blendu")}</h3>
              {analysis.all_tests.length === 0 ? (
                <p className="mes-hint">{t("Nema uvezenih testova.")}</p>
              ) : (
                <div className="mes-table-wrap">
                  <table className="mes-table">
                  <thead>
                    <tr>
                      <th>{t("Uređaj")}</th>
                      <th>{t("ID")}</th>
                      <th>{t("Datum")}</th>
                      <th>{t("Valjano")}</th>
                      <th>{t("Uzorak")}</th>
                      <th>{t("Masa (g)")}</th>
                      <th>{t("Točaka")}</th>
                      <th>{t("Datoteka")}</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {analysis.all_tests.map((test) => (
                      <tr key={`${test.device}-${test.measurement_id}`}>
                        <td>{test.device_label}</td>
                        <td>#{test.measurement_id}</td>
                        <td>{formatHrDateTime(test.measured_at)}</td>
                        <td>
                          {test.is_valid === true && <span className="badge stock-good">{t("da")}</span>}
                          {test.is_valid === false && <span className="badge badge-danger">{t("ne")}</span>}
                          {test.is_valid == null && <span className="badge badge-muted">—</span>}
                        </td>
                        <td>{test.sample_name || "—"}</td>
                        <td className="fq-num">{test.sample_weight != null ? formatNum(test.sample_weight) : "—"}</td>
                        <td>{test.result_count}</td>
                        <td className="fq-file-cell">{test.source_file || "—"}</td>
                        <td>
                          <button
                            type="button"
                            className="btn-secondary btn-sm"
                            onClick={() => setTab(test.device as AnalysisTab)}
                          >
                            {t("Otvori")}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                </div>
              )}
            </div>
          )}

          {tab === "qc" && (
            <div className="fq-tab-panel mes-section">
              <h3>{t("Povijest QC odluka")}</h3>
              {!analysis.qc_decisions?.length ? (
                <p className="mes-hint">{t("Još nema zabilježenih odluka za ovaj LOT.")}</p>
              ) : (
                <ul className="fq-qc-timeline">
                  {analysis.qc_decisions.map((d) => (
                    <li key={d.decision_id} className={`fq-qc-item fq-qc-${d.decision}`}>
                      <div className="fq-qc-item-head">
                        <strong>{d.decision === "release" ? t("Pušteno") : d.decision === "reject" ? t("Odbijeno") : d.decision}</strong>
                        <span>{formatHrDateTime(d.decided_at)}</span>
                      </div>
                      <p>
                        {d.decided_by}
                        {d.evidence_blend_id != null && (
                          <> · blend #{d.evidence_blend_id}</>
                        )}
                      </p>
                      {d.notes && <p className="fq-muted">{d.notes}</p>}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </>
      )}
    </FlourQualityShell>
  );
};

export default FlourQualityLotPage;
